import React, { useState, useEffect } from "react"
import { 
   ArrowLeftOutlined, 
   CloseOutlined, 
   PlusOutlined 
} from "@ant-design/icons"
import {
   Button,
   Col,
   Form,
   Input,
   Row,
   Spin
} from "antd"
import {
   useNavigate,
   useParams
} from "react-router-dom";
import { t } from "i18next"
import moment from "moment";
import swal from "sweetalert";
import * as _ from "lodash";
import {
   createPDD,
   getPerformanceModules,
   getPDDById,
   updatePDDById,
   submitObjectives,
   approveObjectives,
   markAsQuarterlyReviewed,
   submitSelfEvaluation,
   approveEvaluation
} from "services/pdd";
import { 
   InputDate,
   InputRangeDate
} from "common/elements";
import { Util } from "common/util";
import Enum from "common/enums";
import "./index.css";

export default function InsertAndUpdateForm() {
   const params = new URLSearchParams(document.location.search);

   const [performanceModules, setPerformanceModules] = useState({submittor: {}, approver: {}});
   const [state, setState] = useState({
      loading: false,
      saving: false, 
      submitting: false, 
      submittingObjective: false, 
      approvingObjective: false, 
      reviewingQuarterly: false,
      submittingSelfEvaluation: false,
      appovingEvaluation: false,
      isDuplicating: params.get("action") === "duplicate"
   });
   const [employeeEvaluatioEntries, setEmployeeEvaluatioEntries] = useState([]);

   const navigate = useNavigate();
   const [form] = Form.useForm();
   let { id } = useParams();
   let type = params.get("type");

   let title = id ? "Objectives Setting" : "New Objectives Setting";

   if (type === "self-evaluation") {
      title = "Self-Evaluation";
   } else if (type === "evaluation") {
      title = "Evaluation Discussion";
   } else if (type === "quarterly-review") {
      title = "Quarterly Review";
   }

   const isCanSelfEvaluate = Util.getInstance().hasPermission("self evaluation", "evaluation");
   const isCanEvaluate = Util.getInstance().hasPermission("evaluation", "evaluation");

   useEffect(() => {
      if (id) {
         changeStateByKey("loading", true);
         getPDDById(id, type)
         .then(response => {
            if (response) {
               const data = response.data;

               if (state.isDuplicating) {
                  data.status = Enum.OBJECTIVES_SETTING.PENDING;
               }

               setPerformanceModules(data);
               setEmployeeEvaluatioEntries(data.evaluationEntries);
               form.setFieldsValue({
                  yearOfObjective: data.year ? moment(`01-01-${data.year}`) : moment()
               });
            }
         })
         .finally(() => changeStateByKey("loading", false));
      } else {
         changeStateByKey("loading", true);
         getPerformanceModules(type)
         .then(response => {
            setPerformanceModules(response);
         })
         .finally(() => changeStateByKey("loading", false));
      }
      // eslint-disable-next-line
   }, [])

   const changeStateByKey = (key, value) => {
      setState(prevState => ({...prevState, [`${key}`]: value}));
   }

   const onCreateOrUpdateObjectives = (action) => {
      const formValue = form.getFieldsValue();
      const data = {
         employeeId: performanceModules.employeeId,
         year: moment(formValue.yearOfObjective).year(),
         employeeEvaluatioEntries
      };

      if (state.isDuplicating) {
         id = null;
      }

      changeStateByKey("saving", true);

      if (id) {
         data["action"] = action;
         updatePDDById(id, data)
         .then(() => {
            onBack();
         })
         .finally(() => changeStateByKey("saving", false));
      } else {
         createPDD(data)
         .then(() => {
            onBack();
         })
         .catch(error => {
            if (error.response.status === 400) {
               swal({
                  title: "We're sorry",
                  text: error?.response?.data?.error?.message,
                  icon: "error",
                  dangerMode: true,
               })
            }
         })
         .finally(() => changeStateByKey("saving", false));
      }
   }

   const onSubmitObjectives = () => {
      swal({
         title: "Are you sure?",
         text: "You are about to submit objectives to your manager.",
         icon: "warning",
         buttons: ["Cancel", "Submit"],
         dangerMode: true,
      })
      .then(yesSubmit => {
         if (yesSubmit) {
            const formValue = form.getFieldsValue();
            const data = {
               year: moment(formValue.yearOfObjective).year(),
               employeeEvaluatioEntries
            };

            changeStateByKey("submittingObjective", true);

            if (id) {
               submitObjectives(id, data)
               .then(() => {
                  onBack();
               })
               .finally(() => {
                  changeStateByKey("submittingObjective", false);
               });
            } else {
               data["status"] = "SUBMITTED"
               createPDD(data)
               .then(() => {
                  onBack();
               })
               .catch(error => {
                  if (error.response.status === 400) {
                     swal({
                        title: "We're sorry",
                        text: error?.response?.data?.error?.message,
                        icon: "error",
                        dangerMode: true,
                     })
                  }
               })
               .finally(() => changeStateByKey("submittingObjective", false));
            }
         }
      });
   }

   const onApproveObjectives = () => {
      swal({
         title: "Are you sure?",
         text: "You are about to approve objectives setting.",
         icon: "warning",
         buttons: ["Cancel", "Approve"],
         dangerMode: true,
      })
      .then(yesApprove => {
         if (yesApprove) {
            setState(prevState => ({...prevState, approvingObjective: true}));
            approveObjectives(id)
            .then(() => {
               onBack();
            })
            .finally(() => {
               setState(prevState => ({...prevState, approvingObjective: false}));
            });
         }
      });
   }

   const onMarkAsReviewedQuarterly = () => {
      swal({
         title: "Are you sure?",
         text: "You are about to quarterly review objectives.",
         icon: "warning",
         buttons: ["No", "Required Update"],
         dangerMode: true,
      })
      .then(isRequiredToUpdate => {
         if (isRequiredToUpdate) {
            setState(prevState => ({...prevState, reviewingQuarterly: true}));
            markAsQuarterlyReviewed(id, "REQUIRED_UPDATE")
            .then(() => {
               onBack();
            })
            .finally(() => {
               setState(prevState => ({...prevState, reviewingQuarterly: false}));
            });
         }
      });
   }

   const onSubmitSelfEvaluation = () => {
      swal({
         title: "Are you sure?",
         text: "You are about to submit self evaluation to your manager.",
         icon: "warning",
         buttons: ["Cancel", "Submit"],
         dangerMode: true,
      })
      .then(yesSubmit => {
         if (yesSubmit) {
            changeStateByKey("submittingSelfEvaluation", true);
            const formValue = form.getFieldsValue();
            const data = {
               selfEvalDate: moment(formValue.selfEvalDate).format("YYYY-MM-DD"),
               employeeEvaluatioEntries
            };
            submitSelfEvaluation(id, data)
            .then(() => {
               onBack();
            })
            .finally(() => {
               changeStateByKey("submittingSelfEvaluation", false);
            });
         }
      });
   }

   const onApproveEvaluation = () => {
      swal({
         title: "Are you sure?",
         text: "You are about to approve evaluation of your staff.",
         icon: "warning",
         buttons: ["Cancel", "Approve"],
         dangerMode: true,
      })
      .then(yesSubmit => {
         if (yesSubmit) {
            changeStateByKey("appovingEvaluation", true);
            const formValue = form.getFieldsValue();
            const data = {
               mgrEvalDate: moment(formValue.mgrEvalDate).format("YYYY-MM-DD"),
               employeeEvaluatioEntries
            };
            approveEvaluation(id, data)
            .then(() => {
               onBack();
            })
            .finally(() => {
               changeStateByKey("appovingEvaluation", false);
            });
         }
      });
   }

   const onBack = () => {
      navigate(-1);
   }

   const onChageTextArea = (key, value, performanceModuleId) => {
      const foundPerformanceModuleId = employeeEvaluatioEntries.find((employeeEvaluatioEntry) => employeeEvaluatioEntry.performanceModuleId === performanceModuleId);
      if (foundPerformanceModuleId) {
         foundPerformanceModuleId[key] = value
      } else {
         employeeEvaluatioEntries.push({
            performanceModuleId: performanceModuleId,
            [key]: value
         });
         setEmployeeEvaluatioEntries([...employeeEvaluatioEntries])
      }
   }

   let visibleSubmitAndSaveDraft = false;
   let visibleSaveAndSubmit = false;
   
   if (isCanSelfEvaluate) {
      if (id) {
         if (performanceModules?.status === Enum.OBJECTIVES_SETTING.PENDING) {
            visibleSubmitAndSaveDraft = true;
         }

         if (performanceModules.employeeId === Util.getInstance().getCurrentUser().id && performanceModules?.status === Enum.OBJECTIVES_SETTING.SUBMITTED) {
            visibleSaveAndSubmit = true;
         }
         
         // When status required update that happen from quarterly review
         if (performanceModules?.status === Enum.OBJECTIVES_SETTING.REQUIRED_UPDATE) {
            visibleSubmitAndSaveDraft = true;
         }
      } else {
         // If id has no value then it's create form
         visibleSubmitAndSaveDraft = true;
      }
   }
   
   return (
      <>
         <Form form={form}>
            <Row className="btn-navigation">
               <Col span={12}>
                  <div className="pull-left">
                     <Button onClick={onBack}>
                        <ArrowLeftOutlined />
                        {t("text_back")}
                     </Button>
                     <span className="title">
                        {title}
                     </span>
                  </div>
               </Col>
               <Col span={12}>
                  <div className="pull-right">
                     {
                        visibleSubmitAndSaveDraft && 
                        <>
                           <Button loading={state.submittingObjective} style={{backgroundColor: "rgb(184 184 0)", borderColor: "rgb(184 184 0)", color: "white"}} disabled={state.submittingObjective} onClick={onSubmitObjectives}>Submit Objectives</Button>
                           <Button style={{ marginLeft: 15 }} disabled={state.saving} loading={state.saving} onClick={onCreateOrUpdateObjectives}>Save Draft</Button>
                        </>
                     }

                     {
                        visibleSaveAndSubmit && 
                        <Button style={{backgroundColor: "rgb(184 184 0)", borderColor: "rgb(184 184 0)", color: "white"}} disabled={state.saving} loading={state.saving} onClick={() => onCreateOrUpdateObjectives("submit-back")}>Submit Back</Button>
                     }

                     {
                        isCanEvaluate && type === "review-objective" && 
                        performanceModules?.status === Enum.OBJECTIVES_SETTING.SUBMITTED && 
                        <>
                           <Button disabled={state.saving} style={{backgroundColor: "rgb(184 184 0)", borderColor: "rgb(184 184 0)", color: "white", marginRight: 15}} loading={state.saving} onClick={() => onCreateOrUpdateObjectives("send-back")}>Send Back</Button>
                           <Button disabled={state.approvingObjective} loading={state.approvingObjective} style={{ marginRight: 15 }} onClick={onApproveObjectives}>Approve</Button>
                           <Button disabled={state.approvingObjective} style={{backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "white"}} loading={state.approvingObjective} onClick={onApproveObjectives}>Reject</Button>
                        </>
                     }

                     {
                        isCanEvaluate && type === "quarterly-review" && 
                        performanceModules?.status === Enum.OBJECTIVES_SETTING.APPROVED && <Button disabled={state.reviewingQuarterly} loading={state.reviewingQuarterly} onClick={onMarkAsReviewedQuarterly}>Mark as Reviewed</Button>
                     }

                     {
                        isCanSelfEvaluate && type === "self-evaluation" && performanceModules.evaluateStatus !== "APPROVED" && 
                        <Button loading={state.submittingSelfEvaluation} disabled={state.submittingSelfEvaluation} onClick={onSubmitSelfEvaluation}>Submit Self Evaluation</Button>
                     }

                     {
                        isCanEvaluate && type === "evaluation" && performanceModules.evaluateStatus === "SUBMITTED" && 
                        <Button loading={state.appovingEvaluation} disabled={state.appovingEvaluation} onClick={onApproveEvaluation}>Approve Evaluation</Button>
                     }
                  </div>
               </Col>
            </Row>
            {
               state.loading ? <Spin spinning={state.loading} style={{ display: "flex", height: "84vh", justifyContent: 'center', width: "100%", alignItems: "center" }} /> :
                  <div style={{ padding: 15, height: "84vh", overflowY: "auto" }}>

                     <div style={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', marginLeft: 5 }}>
                        <img
                           style={{ width: "60px", height: "60px", objectFit: "cover" }}
                           src="/logo.png"
                           alt="Logo"
                        />
                        <h2 style={{
                           fontSize: 14,
                           fontWeight: 500,
                           marginLeft: 15
                        }}>Norwegian People's Aid Cambodia</h2>
                     </div>

                     <p style={{ fontSize: 18, fontWeight: 600, color: "#1d8747", marginTop: 15 }}>Performance and Development Dialogue / ការសន្ទនាពីការងារ និងការអភិវឌ្ឍសមត្ថភាព</p>
                     <table className="performance-evaluation" id="salary-scale">
                        <tbody>
                           <tr>
                              <td colSpan={3} style={{ backgroundColor: "#ebf1dd" }}>
                                 <div style={{ paddingLeft: 7 }} dangerouslySetInnerHTML={{ __html: performanceModules.title }} />
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 <p className="objectives-setting-p">Name of employee: <span className="objectives-setting-sub-p">{performanceModules?.employee?.fullName}</span></p>
                                 <p className="objectives-setting-sub-p">ឈ្មោះបុគ្គលិក</p>
                              </td>
                              <td>
                                 <p className="objectives-setting-p">Position: <span className="objectives-setting-sub-p">{performanceModules?.employee?.position}</span></p>
                                 <p className="objectives-setting-sub-p">តួនាទី</p>
                              </td>
                              <td>
                                 <p className="objectives-setting-p">Dept./programme/unit: <span className="objectives-setting-sub-p">{performanceModules?.employee?.department}</span></p>
                                 <p className="objectives-setting-sub-p">នាយកដ្ឋាន/កម្មវិធី/ផ្នែក</p>
                              </td>
                           </tr>
                           {
                              isCanSelfEvaluate && [null, "object-setting"].includes(type) && 
                              <tr>
                                 <td>
                                    <>
                                       <div style={{ display: "flex", alignItems: "center" }}>
                                          <p className="objectives-setting-p" >Year of Objective Setting: {performanceModules.year}</p>
                                          <InputDate
                                             name="yearOfObjective"
                                             placeholder={"Select year"}
                                             initialValue={ performanceModules.year ? moment(`01-01-${performanceModules.year}`) : ( params.get("year") !== 'null' ? moment(`01-01-${params.get("year")}`) : moment() ) }
                                             format={"YYYY"}
                                             picker="year"
                                             required={true}
                                             errorMessage={"Please select year"}
                                             style={{ margin: "0px 0px 0px 3px" }}
                                          />
                                       </div>
                                       <p className="objectives-setting-sub-p">គោលបំណងសម្រាប់ឆ្នាំ</p>
                                    </>

                                    {
                                       type === "self-evaluation" && 
                                       <>
                                          <div style={{ display: "flex", alignItems: "center" }}>
                                             <p className="objectives-setting-p" >Date of Self-Evaluation: {performanceModules.selfEvalDate ? moment(performanceModules.selfEvalDate).format("DD/MM/YYYY") : ""}</p>
                                             <InputDate
                                                name="selfEvalDate"
                                                placeholder={"Select date"}
                                                defaultValue={performanceModules.selfEvalDate ? moment(performanceModules.selfEvalDate) : moment()}
                                                format={"DD/MM/YYYY"}
                                                style={{ margin: "0px 0px 0px 3px" }}
                                             />
                                          </div>
                                          <p className="objectives-setting-sub-p">ថ្ងៃវាយតម្លៃផ្ទាល់</p>
                                       </>
                                    }
                                 </td>
                                 <td></td>
                                 <td></td>
                              </tr>
                           }

                           {/* {
                              isCanQuarterlyReview && type === "quarterly-review" && 
                              <tr>
                                 <td>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                       <p className="objectives-setting-p" >Date of Quarterly Review: {performanceModules.reviewedDate ? moment(performanceModules.reviewedDate).format("DD/MM/YYYY") : ""}</p>
                                       {
                                          !performanceModules.reviewedDate && 
                                          <InputDate
                                             name="reviewedDate"
                                             placeholder={"Select date"}
                                             initialValue={moment()}
                                             format={"DD/MM/YYYY"}
                                             required={true}
                                             errorMessage={"Please select year"}
                                             style={{ margin: "0px 0px 0px 3px" }}
                                          />
                                       }
                                    </div>
                                    <p className="objectives-setting-sub-p">ថ្ងៃពិនិត្យឡើងវិញប្រចាំត្រីមាស</p>
                                 </td>
                                 <td></td>
                                 <td></td>
                              </tr>
                           } */}

                           {
                              isCanEvaluate && type === "evaluation" && 
                              <>
                                 <tr>
                                    <td>
                                       <p className="objectives-setting-p">Manager/immediate superior: <span className="objectives-setting-sub-p">{performanceModules?.employee?.supervisor}</span></p>
                                       <p className="objectives-setting-sub-p">ឈ្មោះប្រធានគ្រប់គ្រងផ្ទាល់</p>
                                    </td>
                                    <td>
                                       <p className="objectives-setting-p">Other contributors to evaluation of employee: <span className="objectives-setting-sub-p"></span></p>
                                       <p className="objectives-setting-sub-p">អ្នកចូលរួមផ្សេងទៀតក្នុងការវាយតម្លៃ</p>
                                    </td>
                                    <td>
                                       <div style={{ display: "flex", alignItems: "center" }}>
                                          <p className="objectives-setting-p" >Date of PDD meeting: </p>
                                          <InputDate
                                             name="mgrEvalDate"
                                             placeholder={"Select date"}
                                             format={"DD/MM/YYYY"}
                                             required={true}
                                             errorMessage={"Please select date!"}
                                             style={{ margin: "0px 0px 0px 3px" }}
                                          />
                                       </div>
                                       <p className="objectives-setting-sub-p">ថ្ងៃប្រជុំ</p>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td>
                                       <p className="objectives-setting-p">Context for the PDD meeting: <span className="objectives-setting-sub-p"></span></p>
                                       <p className="objectives-setting-sub-p">បរិបទសម្រាប់ការប្រជុំ៖</p>
                                       <p className="objectives-setting-p" style={{ marginTop: 10 }}>Trial period: <span className="objectives-setting-sub-p"></span></p>
                                       <p className="objectives-setting-sub-p">អំឡុងពេលសាកល្បងការងារ៖</p>
                                    </td>
                                    <td colSpan={2}>
                                       <p className="objectives-setting-p">Context for the PDD meeting: <span className="objectives-setting-sub-p"></span></p>
                                       <p className="objectives-setting-sub-p">បរិបទសម្រាប់ការប្រជុំ៖</p>
                                       <p className="objectives-setting-p" style={{marginTop: 10}}>Contract period: <span className="objectives-setting-sub-p"></span></p>
                                       <p className="objectives-setting-sub-p">កំឡុងពេលនៃកិច្ចសន្យា៖</p>
                                    </td>
                                 </tr>
                              </>
                           }
                           
                           {
                              performanceModules.level1s && performanceModules.level1s.map((level1) => {
                                 let colSpanLevel1;
                                 let colSpanLevel1Action1;
                                 let level1NumberOfAction = 0;
                                 const numberChildLevel1 = level1.level2s.length;

                                 if (level1.action1 && level1.action2) {
                                    colSpanLevel1 = 0;
                                    colSpanLevel1Action1 = 0;
                                    level1NumberOfAction = 2;
                                 } else if (level1.action1) {
                                    colSpanLevel1 = 0;
                                    colSpanLevel1Action1 = 2;
                                    level1NumberOfAction = 1;
                                 } else {
                                    colSpanLevel1 = 3;
                                    colSpanLevel1Action1 = 0;
                                 }

                                 return <React.Fragment key={level1.id}>
                                    <tr key={level1.id} style={{ backgroundColor: "#ebf1dd" }}>
                                       <td colSpan={colSpanLevel1}>
                                          <Title title={level1.title} />
                                       </td>

                                       {
                                          level1.action1 && 
                                          <td colSpan={colSpanLevel1Action1}>
                                             <Title title={level1.action1} />
                                          </td>
                                       }

                                       {
                                          level1.action2 && 
                                          <td>
                                             <Title title={level1.action2} />
                                          </td>
                                       }
                                    </tr>
                                    
                                    {
                                       level1.layout === "horizontal" ?
                                       <RenderHorizontalRow parentModule={level1} performanceModules={level1.level2s} onChageTextArea={onChageTextArea} />
                                       :
                                       level1.level2s.map((level2, level2Index) => {
                                          return <React.Fragment key={level2.id}>
                                             {/* ================ START RENDER ROW LEVEL 2 ================  */}
                                             {
                                                level2.action1 && level2.action2 ?
                                                   <tr style={{ backgroundColor: "#ebf1dd" }}>
                                                      <td>
                                                         <Title title={level2.title} />
                                                      </td>
                                                      <td>
                                                         <Title title={level2.action1} />
                                                      </td>
                                                      <td>
                                                         <Title title={level2.action2} />
                                                      </td>
                                                   </tr>
                                                   :
                                                   level2.action1 ?
                                                      <tr style={{ backgroundColor: "#ebf1dd" }}>
                                                         <td>
                                                            <Title title={level2.title} />
                                                         </td>
                                                         <td colSpan={2}>
                                                            <Title title={level2.action1} />
                                                         </td>
                                                      </tr>
                                                      :
                                                      <>
                                                         {
                                                            level2.reportable ?
                                                               <>
                                                                  {
                                                                     level1NumberOfAction === 2 &&
                                                                     <tr style={{ backgroundColor: "#ebf1dd" }}>
                                                                        <td>
                                                                           <Title title={level2.title} />
                                                                        </td>
                                                                        {
                                                                           level2Index === 0 &&
                                                                           <>
                                                                              <td rowSpan={numberChildLevel1}>
                                                                                 {isCanSelfEvaluate && <Input.TextArea placeholder="Enter your description here ...." defaultValue={level1.content1} onChange={(event) => onChageTextArea("content1", event.target.value, level1.id)} />}
                                                                              </td>
                                                                              <td rowSpan={numberChildLevel1}>
                                                                                 {isCanEvaluate && <Input.TextArea placeholder="Enter your description here ...." defaultValue={level1.content2} onChange={(event) => onChageTextArea("content2", event.target.value, level1.id)} />}
                                                                              </td>
                                                                           </>
                                                                        }
                                                                     </tr>
                                                                  }

                                                                  {
                                                                     level1NumberOfAction === 1 && 
                                                                     <tr>
                                                                        <td>
                                                                           <Title title={level2.title} />
                                                                        </td>
                                                                        {
                                                                           level2Index === 0 && 
                                                                           <td colSpan={2} rowSpan={numberChildLevel1}>
                                                                              {isCanSelfEvaluate && <Input.TextArea placeholder="Enter your description here ...." defaultValue={level1.content1} onChange={(event) => onChageTextArea("content1", event.target.value, level1.id)} />}
                                                                           </td>
                                                                        }
                                                                     </tr>
                                                                  }
                                                                  {
                                                                     level1NumberOfAction === 0 && 
                                                                     <tr style={{ backgroundColor: "#ebf1dd" }}>
                                                                        <td colSpan={3} style={{padding: 0}}>
                                                                           <div>
                                                                              <Title title={level2.title} style={{borderBottom: "1px solid #000", paddingLeft: 5}} />
                                                                              {isCanSelfEvaluate && <InputDescription fieldName="content1" performanceModule={level2} onChageTextArea={onChageTextArea}  />}
                                                                           </div>
                                                                        </td>
                                                                     </tr>
                                                                  }
                                                               </>
                                                               :
                                                               <tr style={{ backgroundColor: "#ebf1dd" }}>
                                                                  <td colSpan={3}>
                                                                     <Title title={level2.title} />
                                                                  </td>
                                                               </tr>
                                                         }
                                                      </>
                                             }
                                             {/* ================ END RENDER ROW LEVEL 2 ================  */}

                                             {/* ================ START RENDER ROW LEVEL 3 ================  */}

                                             {
                                                level2.layout === "horizontal" ? 
                                                <RenderHorizontalRow parentModule={level2} performanceModules={level2.level3s} onChageTextArea={onChageTextArea} />
                                                :
                                                level2.level3s.map((level3, level3Index) => {
                                                   const numberChildLevel2 = level2.level3s.length;

                                                   let level2NumberOfAction = 0;
                                                   if (level2.action1 && level2.action2) {
                                                      level2NumberOfAction = 2;
                                                   } else if (level2.action1) {
                                                      level2NumberOfAction = 1;
                                                   }

                                                   if (level1NumberOfAction === 2 || level2NumberOfAction === 2) {
                                                      return <tr key={level3.id}>
                                                         <td>
                                                            <Title title={level3.title} style={{paddingLeft: 20}} />
                                                         </td>
                                                         {
                                                            level3Index === 0 && 
                                                            <>
                                                               <td rowSpan={numberChildLevel2}>
                                                                  {isCanSelfEvaluate && <InputDescription fieldName="content1" performanceModule={level2} onChageTextArea={onChageTextArea} />}
                                                               </td>
                                                               <td rowSpan={numberChildLevel2}>
                                                                  {isCanEvaluate && <InputDescription fieldName="content2" performanceModule={level2} onChageTextArea={onChageTextArea} />}
                                                               </td>
                                                            </>
                                                         }
                                                      </tr>
                                                   } else if (level2NumberOfAction === 2) {
                                                      return <tr key={level3.id}>
                                                         <td>
                                                            <Title title={level3.title} style={{paddingLeft: 20}} />
                                                         </td>
                                                         <td colSpan={2}>
                                                            {isCanSelfEvaluate && <Input.TextArea placeholder="Enter your description here ...." defaultValue={level3.content1 && level3.content1} onChange={(event) => onChageTextArea("content1", event.target.value, level3.id)} />}
                                                         </td>
                                                      </tr>
                                                   } else {
                                                      return <React.Fragment key={level3.id}>
                                                         <tr>
                                                            <td colSpan={3}>
                                                               <Title title={level3.title} style={{paddingLeft: 20}} />
                                                            </td>
                                                         </tr>
                                                         <tr>
                                                            <td colSpan={3}>
                                                               {isCanSelfEvaluate && <InputDescription fieldName="content1" performanceModule={level3} onChageTextArea={onChageTextArea} /> }
                                                            </td>
                                                         </tr>
                                                      </React.Fragment>
                                                   }
                                                })
                                             }
                                             {/* ================ END RENDER ROW LEVEL 3 ================  */}
                                          </React.Fragment>
                                       })
                                    }
                                 </React.Fragment>
                              })
                           }
                        </tbody>
                     </table>
                     {
                        performanceModules.status === "APPROVED" && 
                        <Footer submittor={performanceModules.submittor} approver={performanceModules.approver} />
                     }
                  </div>
            }
         </Form>
      </>
   )
}

const Footer = ({submittor, approver}) => {
   return (
      <footer className="footer-container">
         <div className="footer-info">
            <div className="footer-label">Submittor</div>
            <div>Name: {submittor.name}</div>
            <div>Position: {submittor.position}</div>
            <div>Date: {submittor.date}</div>
         </div>
         <div className="footer-info">
            <div className="footer-label">Approver</div>
            <div>Name: {approver.name}</div>
            <div>Position: {approver.position}</div>
            <div>Date: {approver.date}</div>
         </div>
      </footer>
   );
 };

function RenderHorizontalRow({parentModule, performanceModules, onChageTextArea}) {
   const [entries, setEntries] = React.useState([]);

   React.useEffect(() => {
      try {
         if (parentModule.content1) {
            setEntries(JSON.parse(parentModule.content1));
         }
      } catch (error) {}

      // eslint-disable-next-line
   }, []);

   performanceModules = _.sortBy(performanceModules, "order");

   const onChangeValue = (row, moduleId, cellValue) => {
      const foundEntryRow = entries.find((entry, index) => index === row);
      const foundModuleEntry = foundEntryRow.modules.find(module => module.moduleId === moduleId);
      foundModuleEntry["content"] = cellValue;

      onChageTextArea("content1", JSON.stringify(entries), parentModule.id);

   };

   const onSelectDate = (row, moduleId, cellDateValues) => {
      const foundEntryRow = entries.find((entry, index) => index === row);
      const foundModuleEntry = foundEntryRow.modules.find(module => module.moduleId === moduleId);
      if (cellDateValues) {
         foundModuleEntry["content"] = [cellDateValues[0].format("YYYY-MM-DD"), cellDateValues[1].format("YYYY-MM-DD")];
      } else {
         foundModuleEntry["content"] = null;
      }

      onChageTextArea("content1", JSON.stringify(entries), parentModule.id);
   };

   const onAddNew = (performanceModules) => {
      const modules = [];
      performanceModules.forEach(performanceModule => {
         modules.push({moduleId: performanceModule.id, content: "", inputType: performanceModule.inputType});
      });

      const newEntries = [...entries, {uniqueId: Date.now(), modules}];
      setEntries(newEntries);
   }

   const onRemove = (uniqueId) => {
      const foundAtIndex = entries.findIndex(entry => entry.uniqueId === uniqueId);

      entries.splice(foundAtIndex, 1);

      setEntries([...entries]);

      onChageTextArea("content1", JSON.stringify(entries), parentModule.id);
   }

   return <tr>
      <td colSpan={3} style={{padding: 0}}>
         <table style={{tableLayout: "fixed", width: "100%"}}>
            <tbody>
               <tr>
                  {
                     performanceModules.map((performanceModule, key) => 
                        <td key={performanceModule.id} style={{width: `${performanceModules.length}%`, borderWidth: 0, borderLeftWidth: key === 0 ? 0 : 1, padding: 0}}>
                           {
                              // Display only description when parent enable add new option
                              parentModule.enableMultiRow ?
                              <div dangerouslySetInnerHTML={{ __html: performanceModule.title }} style={{paddingLeft: 5}} />
                              :
                              <div>
                                 <div dangerouslySetInnerHTML={{ __html: performanceModule.title }} style={{paddingLeft: 5, borderBottom: "1px solid #000"}} />
                                 <Input.TextArea rows={8} placeholder="Enter your description here....." defaultValue={performanceModule.content1 ? performanceModule.content1 : performanceModule.defaultDescription} onChange={(event) => onChageTextArea("content1", event.target.value, performanceModule.id)} />
                              </div>
                           }
                        </td>
                     )
                  }
               </tr>
               {
                  entries.map(({uniqueId, modules}, entryKey) => {

                     return <tr key={uniqueId}>
                        {
                           modules.map(({moduleId, content, inputType}, columnKey) => 
                              <td key={columnKey} style={{borderLeftWidth: columnKey === 0 ? 0 : 1, borderRightWidth: columnKey === modules.length - 1 ? 0 : 1}}>
                                 <div style={{position: "relative"}}>
                                    {
                                       inputType === "date" ?
                                       <div style={{paddingLeft: 15, paddingRight: 15}}>
                                          <InputRangeDate
                                             name={`date-${uniqueId}-${moduleId}-${columnKey}`}
                                             placeholder={"Select timeline"}
                                             initialValue={content ? [moment(content[0]), moment(content[1])] : []}
                                             format={"DD/MM/YYYY"}
                                             allowClear={true}
                                             separator={"~"}
                                             style={{width: "100%"}}
                                             onChange={date => onSelectDate(entryKey, moduleId, date)}
                                          />
                                       </div>
                                       :
                                       <Input.TextArea key={columnKey} rows={3} placeholder="Enter your description here....." defaultValue={content}  onChange={(event) => onChangeValue(entryKey, moduleId, event.target.value)} />
                                    }

                                    {
                                       columnKey === modules.length - 1 && 
                                       <Button type="primary" danger shape="circle" icon={<CloseOutlined />} onClick={() => onRemove(uniqueId)} style={{marginLeft: 15, position: "absolute", top: 0, right: 0}} />
                                    }
                                 </div>
                              </td>
                           )
                        }
                     </tr>;
                  })
               }
            </tbody>
            {
               parentModule.enableMultiRow === 1 && 
               <tfoot>
                  <tr>
                     <td colSpan={performanceModules.length} style={{borderWidth: 0, borderTopWidth: 1, paddingRight: 10, paddingLeft: 10}}>
                        <Button type="dashed" icon={<PlusOutlined />} style={{ width: "100%" }} onClick={() => onAddNew(performanceModules)}>
                           {t("text_add_new")}
                        </Button>
                     </td>
                  </tr>
               </tfoot>
            }
         </table>
      </td>
   </tr>;
}

function Title({title, style}) {
   return <div style={style} dangerouslySetInnerHTML={{ __html: title }} />;
}

function InputDescription({fieldName, performanceModule, onChageTextArea}) {
   return <Input.TextArea 
      placeholder="Enter your description here ...."
      rows={performanceModule.numRow} 
      defaultValue={performanceModule[fieldName]} 
      onChange={event => onChageTextArea(fieldName, event.target.value, performanceModule.id)} 
   />;
}

Title.defaultProps = {
   style: {
      paddingLeft: 5
   }
};
